import Tesseract from 'tesseract.js';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js Worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

/**
 * DevTracker DAIV-X Resume Parser
 * Version: v2.1.0 (Multi-Format)
 * Protocol: NEURAL_EXTRACTION_V5
 */

/**
 * Extracts raw text from an Image file (OCR)
 */
export const extractTextFromImage = async (file) => {
  const { data: { text } } = await Tesseract.recognize(file, 'eng', {
    logger: m => console.log(`[OCR] ${m.status}: ${Math.round(m.progress * 100)}%`)
  });
  return text;
};

/**
 * Extracts raw text from a PDF file
 */
export const extractTextFromPDF = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let fullText = '';
  
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const strings = content.items.map(item => item.str);
    fullText += strings.join(' ') + '\n';
  }
  
  return fullText;
};

/**
 * Unified entry point for file-based extraction
 */
export const extractFromBlob = async (file) => {
  const mime = file.type;
  
  if (mime.includes('image')) {
    return await extractTextFromImage(file);
  } else if (mime.includes('pdf')) {
    return await extractTextFromPDF(file);
  } else if (mime.includes('text') || file.name.endsWith('.txt')) {
    return await file.text();
  }
  
  throw new Error(`UNSUPPORTED_FORMAT: Protocol does not support ${mime}`);
};

export const parseResumeContent = (text) => {
  const result = {
    experience: [],
    education: [],
    projects: [],
    summary: "",
    bio: "",
    name: "",
    email: "",
    phone: "",
    linkedin: ""
  };

  // 1. Normalize text
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  
  // 2. Identify Contact Details (Regex Based)
  // Ultimate-tier phone regex: supports 7-15 digits, continuous or separated, plus international prefixes
  const phoneRegex = /(?:\+?\d{1,4}[-.\s]?)?\(?\d{2,5}\)?[-.\s]?\d{3,5}[-.\s]?\d{3,6}|\b\d{10,15}\b/;
  const linkedinRegex = /linkedin\.com\/in\/[a-zA-Z0-9_-]+/;
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
  
  const phoneMatch = text.match(phoneRegex);
  if (phoneMatch) result.phone = phoneMatch[0];

  const linkedinMatch = text.match(linkedinRegex);
  if (linkedinMatch) result.linkedin = `https://${linkedinMatch[0]}`;

  const emailMatch = text.match(emailRegex);
  if (emailMatch) result.email = emailMatch[0];

  // 3. Name Discovery (Improved Heuristic)
  const roleKeywords = ['Developer', 'Engineer', 'Architect', 'Scientist', 'Analyst', 'Manager', 'Lead', 'Consultant', 'Intern', 'Specialist'];
  const skipKeywords = ['check', 'resume', 'cv', 'profile', 'dossier', 'here', 'above', 'below', 'this'];
  
  for (let i = 0; i < Math.min(lines.length, 5); i++) {
    const line = lines[i];
    const lower = line.toLowerCase();
    
    // Skip if contains contact info
    if (line.includes('@') || line.match(/\d{7,}/)) continue;
    // Skip if contains intro words
    if (skipKeywords.some(kw => lower.includes(kw))) continue;
    // Skip if too long
    if (line.length > 40) continue;
    
    // Likely a name
    if (!roleKeywords.some(kw => lower.includes(kw.toLowerCase()))) {
      result.name = line.split('|')[0].trim();
      break;
    }
  }

  // 4. Extract Bio/Role
  const bioLine = lines.find(line => roleKeywords.some(kw => line.toLowerCase().includes(kw.toLowerCase())));
  if (bioLine) result.bio = bioLine.substring(0, 100);

  // 5. Section Boundary Detection & Contextual Mapping
  let currentSection = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lowerLine = line.toLowerCase();

    // Section Triggers
    if (lowerLine.match(/experience|work history|employment|career/)) { currentSection = 'experience'; continue; }
    if (lowerLine.match(/education|academic|qualifications|university|college/)) { currentSection = 'education'; continue; }
    if (lowerLine.match(/projects|technical work|portfolio|open source/)) { currentSection = 'projects'; continue; }
    if (lowerLine.match(/summary|objective|about me|professional profile/)) { currentSection = 'summary'; continue; }
    if (lowerLine.match(/skills|technologies|tools/)) { currentSection = 'summary'; continue; } // Map skills to summary for now

    // Extraction Logic based on Section
    if (currentSection === 'experience') {
      // Look for Date Patterns (Support - and –)
      const dateMatch = line.match(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s?\d{4}\s?[-–]\s?(\d{4}|Present|Current|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i);
      
      if (dateMatch) {
        // High-fidelity heuristic: The previous line is likely the company/role
        const contextLine = lines[i-1] || "";
        const roleLine = lines[i+1] || "";
        
        if (contextLine.length > 3 && contextLine.length < 100) {
          result.experience.push({
            role: roleLine.length < 50 ? roleLine : "Professional Role Detected",
            company: contextLine,
            duration: dateMatch[0],
            desc: "Extracted via contextual DAIV-X multi-line audit."
          });
        }
      } else if ((line.includes('|') || line.includes(' - ')) && line.length < 120) {
        // Direct format fallback
        const parts = line.split(/[|\-]/).map(p => p.trim());
        if (parts.length >= 2) {
          result.experience.push({
            role: parts[0],
            company: parts[1],
            duration: "Neural Detection Active",
            desc: "Extracted via high-fidelity record linkage."
          });
        }
      }
    }

    if (currentSection === 'education') {
      const eduKeywords = ['Bachelor', 'Master', 'B-Tech', 'M-Tech', 'Degree', 'University', 'College', 'Institute', 'B.S.', 'M.S.'];
      if (eduKeywords.some(kw => line.includes(kw))) {
        const parts = line.split(/[,\-|]/).map(p => p.trim());
        result.education.push({
          degree: parts[2] || parts[0] || "Degree Technical",
          school: parts[0] || "Detected Institution",
          year: text.match(/\d{4}/)?.[0] || "Auto-Scanned"
        });
      }
    }

    if (currentSection === 'projects') {
      if (line.length > 5 && line.length < 80 && (line.includes('|') || (line.includes(' | ')))) {
        const title = line.split('|')[0].trim();
        result.projects.push({
          title,
          desc: "Technical implementation identified via neural document audit.",
          link: ""
        });
      }
    }

    if (currentSection === 'summary' && result.summary.length < 800) {
      if (!lowerLine.includes('summary') && !lowerLine.includes('skills')) {
        result.summary += (result.summary ? ' ' : '') + line;
      }
    }
  }

  return result;
};

/**
 * Simulates an AI thinking delay
 */
export const simulateNeuralProcessing = async (ms = 3000) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};
